// sound5/cues.js: one-shot cues. Each builds a few short-lived nodes that stop
// themselves; nothing here keeps running.
//   ignite  sub drop + impact + bright shimmer, then a reverse swell that
//           lands on the bloom chord ~1.95 s later (scene.js starts it)
//   whoosh  band-passed noise sweeping across the stereo field
//   horizon a last low gulp, then the engine drops everything to silence
//   brand   a small ignition for the brand star (its motif follows)
//   click / hover / poster  quiet UI sounds, rate-limited
import { biquad, makePan, warmCurve, noiseBuffer, clamp } from './util.js';

export class Cues {
  constructor(eng) {
    this.eng = eng;
    this.last = {};
    const c = eng.ctx;
    this.white = noiseBuffer(c, 1.5, 'white', eng.rng, 0.3);
    this.pink = noiseBuffer(c, 3, 'pink', eng.rng, 0.3, 24000);
    this.warm = warmCurve(1025, 1.6, 0.1);
    // cue bus: straight to the master (no transition dip), plus the hall
    const m = eng.master;
    this.bus = c.createGain();
    this.bus.gain.value = 1;
    this.bus.connect(m.fx);
    this.wet = c.createGain();
    this.wet.gain.value = 1;
    this.wet.connect(m.send);
  }

  // rate limit per cue name (seconds)
  ok(name, gap, now) {
    if (this.last[name] != null && now - this.last[name] < gap) return false;
    this.last[name] = now;
    return true;
  }

  gain(v) { const g = this.eng.ctx.createGain(); g.gain.value = v; return g; }

  // envelope on a fresh gain: attack time constant, hold, decay time constant
  env(t, peak, atk, hold, dec) {
    const g = this.gain(0);
    g.gain.setValueAtTime(0, t);
    g.gain.setTargetAtTime(peak, t, atk);
    g.gain.setTargetAtTime(0, t + hold, dec);
    return g;
  }

  out(node, dry, wet, pan) {
    let n = node;
    if (pan) { const p = makePan(this.eng.ctx, clamp(pan, -1, 1)); n.connect(p.node); n = p.node; }
    if (dry > 0) { const g = this.gain(dry); n.connect(g); g.connect(this.bus); }
    if (wet > 0) { const g = this.gain(wet); n.connect(g); g.connect(this.wet); }
  }

  noise(buf, t, dur, offset = 0) {
    const s = this.eng.ctx.createBufferSource();
    s.buffer = buf;
    s.loop = true;
    s.start(t, offset);
    s.stop(t + dur);
    return s;
  }

  // a sine whose pitch falls exponentially from f0 to f1 over `fall` seconds
  drop(t, f0, f1, fall, peak, dec, dur, harm = 0) {
    const c = this.eng.ctx, o = c.createOscillator(), sh = c.createWaveShaper(), g = this.env(t, peak, 0.003, 0.03, dec);
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + fall);
    o.start(t);
    o.stop(t + dur);
    if (harm > 0) { sh.curve = this.warm; const pre = this.gain(harm); o.connect(pre); pre.connect(sh); sh.connect(g); } else o.connect(g);
    return g;
  }

  ignite(t) {
    const e = this.eng, b = e.bank;
    // 1. sub drop: 90 -> 27 Hz with a warm octave for small speakers
    this.out(this.drop(t, 90, 27, 1.5, 0.95, 1.1, 5.5), 1, 0.12);
    this.out(this.drop(t, 180, 54, 1.5, 0.22, 0.5, 3, 1.4), 1, 0.2);
    // 2. impact: a low noise burst
    const imp = biquad(e.ctx, 'lowpass', 700, 0.7), ig = this.env(t, 0.9, 0.002, 0.01, 0.13);
    this.noise(this.pink, t, 1.2).connect(imp);
    imp.connect(ig);
    this.out(ig, 1, 0.5);
    // 3. bright shimmer: a glitter of bells up the D add9 chord and an air burst
    const glit = [86, 88, 90, 93, 98, 100];
    glit.forEach((m, i) => {
      if (m > 93) return e.shots.play(b.get('bell', m - 12), e.B.fx, t + 0.02 + i * 0.045, 0.3, (i / 5 - 0.5) * 1.2, 2);
      return e.shots.play(b.get('bell', m), e.B.fx, t + 0.02 + i * 0.045, 0.45, (i / 5 - 0.5) * 1.2);
    });
    const hp = biquad(e.ctx, 'highpass', 5200, 0.7), ag = this.env(t, 0.22, 0.004, 0.02, 0.7);
    this.noise(this.white, t, 4).connect(hp);
    hp.connect(ag);
    this.out(ag, 0.25, 1);
    // 4. reverse swell into the bloom (lands at t + 1.95)
    const sw = b.get('swell');
    e.shots.play(sw, e.B.fx, t + 1.95 - sw.duration, 0.55);
  }

  whoosh(t, dir = 1) {
    const e = this.eng, c = e.ctx;
    const bp = biquad(c, 'bandpass', 300, 1.1), g = this.env(t, 0.5, 0.09, 0.18, 0.4);
    bp.frequency.setValueAtTime(300, t);
    bp.frequency.exponentialRampToValueAtTime(2600, t + 0.3);
    bp.frequency.exponentialRampToValueAtTime(380, t + 1.5);
    this.noise(this.pink, t, 2.2, e.rng() * 2).connect(bp);
    bp.connect(g);
    const p = makePan(c, -0.6 * dir);
    if (p.pan) { p.pan.setValueAtTime(-0.6 * dir, t); p.pan.linearRampToValueAtTime(0.6 * dir, t + 1.2); }
    g.connect(p.node);
    this.out(p.node, 1, 0.45);
  }

  horizon(t) {
    // a short low gulp; the engine ducks everything (this included) at t + 0.14
    this.out(this.drop(t, 70, 22, 0.22, 0.8, 0.25, 1.2, 1.2), 1, 0);
  }

  brand(t) {
    const e = this.eng;
    this.out(this.drop(t, 62, 37, 0.8, 0.5, 1.2, 4.5), 1, 0.2);
    [81, 86, 90, 93].forEach((m, i) => e.shots.play(e.bank.get('bell', m), e.B.fx, t + 0.03 + i * 0.06, 0.28, (i / 3 - 0.5)));
    const hp = biquad(e.ctx, 'highpass', 6000, 0.7), ag = this.env(t, 0.12, 0.004, 0.02, 0.5);
    this.noise(this.white, t, 3, 0.5).connect(hp);
    hp.connect(ag);
    this.out(ag, 0.2, 1);
  }

  click(t) {
    this.eng.shots.play(this.eng.bank.get('tick'), this.eng.B.ui, t, 0.5, 0, 1.5);
  }

  hover(t) {
    const c = this.eng.ctx, o = c.createOscillator(), g = this.env(t, 0.05, 0.002, 0.005, 0.045);
    o.frequency.value = 1760;
    o.start(t);
    o.stop(t + 0.4);
    o.connect(g);
    g.connect(this.eng.B.ui.in);
  }

  poster(t) {
    const e = this.eng;
    e.shots.play(e.bank.get('tick'), e.B.ui, t, 0.6, -0.2, 0.8);
    e.shots.play(e.bank.get('tick'), e.B.ui, t + 0.035, 0.45, 0.2, 1.25);
    e.shots.play(e.bank.get('bell', 86), e.B.fx, t + 0.08, 0.3, -0.3);
    e.shots.play(e.bank.get('bell', 93), e.B.fx, t + 0.24, 0.3, 0.3);
  }
}
